import { useParams, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import LoadingPage from "../../public/LoadingPage";
import { useGetProcessUIData } from "../../../hooks/entity";
import { useLmcUserView } from "../../../contexts/AuthContext";
import { EventProcessData, Event } from "../../../types/EventPaymentTypes";
import EventService from "../../../services/eventService";
import { toast } from "react-toastify";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiMail,
  HiXCircle,
} from "react-icons/hi";
import { HiTicket, HiArrowLeft } from "react-icons/hi2";
import { MdOutlineLocationOn } from "react-icons/md";
import { StatusBadge } from "../../atoms/StatusBadge";
import LoyaltyCardContainer from "../../atoms/LoyaltyCardContainer";
import BusinessCardFooter from "../../atoms/BusinessCardFooter";
import SecureStorage from "../../../utils/secureStorage";
import { createErrorHandler } from "../../../utils/errorHandler";
import LanguageSelector from "../../atoms/LanguageSelector";
import AddToGoogleCalendarButton from "../../atoms/AddToGoogleCalendarButton";
import BackButton from "../../atoms/eventCard/BackButton";
import { ImExit } from "react-icons/im";
import { CustomButton } from "../../atoms/CustomButton";

const blinkStyle = {
  animation: "blink 1s ease-in-out infinite",
};

const styleSheet = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

interface LocationState {
  ticketData?: {
    ticket_id: string;
    event_code: string;
    ticket_holder_names: string[];
    payment_date: string;
    ticket_quantity: number;
    payment_amount: number;
    payment_identification?: string;
    attendee_email_validation_status?: Record<string, boolean>;
    current_attendee_has_ticket?: Record<string, boolean>;
    current_purchaser_has_ticket?: boolean;
    purchaser_already_has_ticket?: boolean;
    reservation_id?: string;
  };
}

const normalizeHex = (hexColor?: string) => {
  if (!hexColor) return null;
  const hex = hexColor.trim();
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  return hex.startsWith("#") ? hex : `#${hex}`;
};

const adjustHexColor = (hex: string, amount: number) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;

  const hexBody = normalized.replace("#", "");
  const num = parseInt(hexBody, 16);

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const EventDashboardApp = () => {
  const { t } = useTranslation("clientView");
  const { processId } = useParams<{ processId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    dispatch,
    state: { auth },
  } = useLmcUserView();

  const [ticketData, setTicketData] = useState<
    LocationState["ticketData"] | null
  >(null);
  const [isLoadingTicketData, setIsLoadingTicketData] = useState(true);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [userEmail, setuserEmail] = useState("");
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  );

  const {
    data: processData,
    isLoading,
    isError,
  } = useGetProcessUIData(processId as string, {
    enabled: !!processId,
    onSuccess: (data) => {
      dispatch({
        type: "ENTITY",
        payload: data,
      });
    },
  });

  useEffect(() => {
    if (!auth.isAuthenticated && processId) {
      navigate(`/event/${processId}`, { replace: true });
    }
  }, [auth.isAuthenticated, processId, navigate]);

  const handleLogout = () => {
    dispatch({ type: "LOGOUT", payload: {} });
    navigate(`/event/${processId}`, { replace: true });
  };

  useEffect(() => {
    const fetchTicketData = async () => {
      if (!auth.isAuthenticated || !auth.user_identification || !processData) {
        setIsLoadingTicketData(false);
        return;
      }

      const handleError = createErrorHandler({
        dispatch,
        navigate,
        redirectPath: `/event/${processId}`,
      });

      const credentials = SecureStorage.getAuthCredentials();
      if (!credentials || !credentials.username || !credentials.password) {
        toast.error(t("event.errors.loginFirst"));
        setIsLoadingTicketData(false);
        navigate(`/event/${processId}`, { replace: true });
        return;
      }

      if (hasAttemptedFetch) {
        return;
      }

      setHasAttemptedFetch(true);

      const urlParams = new URLSearchParams(window.location.search);
      const locationState = location.state as LocationState;
      const reservation_id =
        urlParams.get("reservation_id") ||
        locationState?.ticketData?.reservation_id;

      const eventData = processData as EventProcessData;
      const eventCode = eventData.process_data.event_code;
      const entityIdentification = eventData.entity_data.entity_identification;

      const cameFromPaymentFlow = urlParams.has("user_id");

      if (cameFromPaymentFlow) {
        try {
          const verifyResponse = await EventService.verifyReservation(
            auth.user_identification,
            entityIdentification,
            eventCode,
            reservation_id as string
          );

          if (
            !verifyResponse?.data?.element?.success &&
            !verifyResponse?.data?.element?.already_processed
          ) {
            setVerificationError(t("event.errors.paymentCancelled"));
            return;
          }
        } catch (verifyError: any) {
          if (handleError(verifyError)) return;
          const errorMessage =
            verifyError?.response?.data?.message ||
            t("event.errors.verificationFailed");
          setVerificationError(errorMessage);
          return;
        }
      }

      try {
        setIsLoadingTicketData(true);

        if (reservation_id) {
          const response = await EventService.getReservationDetails(
            auth.user_identification,
            entityIdentification,
            reservation_id
          );
          const details = response?.data?.element;

          if (details) {
            const ticketHolderEmails = details.ticket_holder_emails || [
              auth.user_email || auth.user_identification,
            ];
            const purchaserEmail = ticketHolderEmails[0];
            setuserEmail(
              purchaserEmail || auth.user_email || auth.user_identification
            );

            setTicketData({
              ticket_id: details.ticket_id,
              event_code: details.event_code,
              ticket_holder_names: ticketHolderEmails,
              payment_date: details.timestamp,
              ticket_quantity:
                details.ticket_quantity ?? ticketHolderEmails.length,
              payment_amount: details.total_amount || 0,
              attendee_email_validation_status:
                details.attendee_email_validation_status,
              current_attendee_has_ticket: details.current_attendee_has_ticket,
              current_purchaser_has_ticket:
                details.current_purchaser_has_ticket,
              purchaser_already_has_ticket:
                details.purchaser_already_has_ticket,
              reservation_id,
            });
          } else {
            navigate(`/event/${processId}`, { replace: true });
          }
        } else {
          const response = await EventService.getMyTickets(
            auth.user_identification,
            entityIdentification,
            eventCode
          );
          const tickets = response?.data?.element?.tickets || [];
          setuserEmail(auth.user_email || auth.user_identification);

          if (tickets.length > 0) {
            const mostRecentTicket = tickets[0];
            const ticketHolderEmails =
              mostRecentTicket.ticket_holder_emails || [
                auth.user_email || auth.user_identification,
              ];

            setTicketData({
              ticket_id: mostRecentTicket.ticket_id,
              event_code: mostRecentTicket.event_code,
              ticket_holder_names: ticketHolderEmails,
              payment_date: mostRecentTicket.timestamp,
              ticket_quantity:
                mostRecentTicket.ticket_quantity ?? ticketHolderEmails.length,
              payment_amount: mostRecentTicket.total_amount || 0,
              payment_identification: mostRecentTicket.payment_unique_link,
              attendee_email_validation_status:
                mostRecentTicket.attendee_email_validation_status,
              current_attendee_has_ticket:
                mostRecentTicket.current_attendee_has_ticket,
              current_purchaser_has_ticket:
                mostRecentTicket.current_purchaser_has_ticket,
            });
          } else {
            navigate(`/event/${processId}`, { replace: true });
          }
        }
      } catch (error: any) {
        console.error("Error fetching ticket data:", error);
        if (handleError(error)) return;
      } finally {
        setIsLoadingTicketData(false);
      }
    };

    if (processData && auth.isAuthenticated) {
      fetchTicketData();
    }
  }, [
    processData,
    auth.isAuthenticated,
    auth.user_identification,
    auth.user_email,
    hasAttemptedFetch,
    navigate,
    processId,
    location.state,
    dispatch,
    t,
  ]);

  const eventData = processData as EventProcessData | undefined;
  const eventCode = eventData?.process_data?.event_code;
  const eventDetails: Event | undefined = eventCode
    ? (eventData?.process_data?.[eventCode] as Event | undefined)
    : undefined;
  const eventSettings = eventDetails?.event_data?.settings;
  const entityData = eventData?.entity_data;
  const purchasedTicketType = ticketData?.ticket_id
    ? eventDetails?.event_data?.ticket_types?.[ticketData.ticket_id]
    : undefined;

  const { gradientStyle, entityBrand } = useMemo(() => {
    const accentColor = entityData?.accent_color || "#0ea5e9";
    const accentBase = normalizeHex(accentColor) || "#0ea5e9";
    const lighter = adjustHexColor(accentBase, 40) || accentBase;
    const darker = adjustHexColor(accentBase, -35) || accentBase;

    const style = {
      backgroundImage: `linear-gradient(135deg, ${lighter}, ${darker})`,
    };

    let brand;
    if (entityData?.entity_logo) {
      brand = (
        <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/40 shadow-sm bg-white">
          <img
            src={entityData.entity_logo}
            alt={entityData.entity_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      );
    } else {
      const initial =
        (entityData?.entity_name || eventSettings?.event_name || "?")
          .trim()
          .charAt(0)
          .toUpperCase() || "?";
      brand = (
        <div className="w-12 h-12 rounded-xl border border-white/40 bg-white/10 flex items-center justify-center text-lg font-semibold text-white shadow-sm">
          {initial}
        </div>
      );
    }

    return { gradientStyle: style, entityBrand: brand };
  }, [
    entityData?.accent_color,
    entityData?.entity_logo,
    entityData?.entity_name,
    eventSettings?.event_name,
  ]);

  if (isLoading || isLoadingTicketData) {
    return <LoadingPage />;
  }

  if (isError || !processData) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t("event.errors.loadingEvent")}
          </h1>
          <p className="text-white/70 mb-4">
            {t("event.errors.loadingEventMessage")}
          </p>
        </div>
      </div>
    );
  }

  if (!eventSettings || !entityData) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t("event.errors.notFound")}
          </h1>
          <p className="text-white/70 mb-4">{t("event.errors.message")}</p>
        </div>
      </div>
    );
  }

  if (verificationError) {
    return (
      <LoyaltyCardContainer
        frontContent={
          <div className="w-full h-full" translate="no">
            <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
              <div className="row-start-1 row-end-2 min-h-0">
                <div
                  className="h-full overflow-y-auto overscroll-contain touch-pan-y"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 backdrop-blur bg-gray-950/90 m-3 sm:m-4">
                    <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7">
                      <div
                        className="absolute inset-0 opacity-90"
                        style={gradientStyle}
                      />
                      <div className="relative z-10 flex flex-col items-center gap-4 text-center">
                        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                          <HiXCircle className="text-red-400" size={48} />
                        </div>
                        <h1 className="text-xl font-bold text-white">
                          {t("event.errors.paymentVerificationTitle")}
                        </h1>
                        <p className="text-sm text-white/80">
                          {verificationError}
                        </p>
                        <CustomButton
                          variant="none"
                          size="lg"
                          onClick={() =>
                            navigate(`/event/${processId}`, { replace: true })
                          }
                          icon={
                            <HiArrowLeft size={18} className="rtl:rotate-180" />
                          }
                          title={t("event.dashboard.backToEvent")}
                          className="mt-4 w-full rounded-md border border-white/20 bg-white/10 font-medium text-white shadow-sm hover:bg-white/20"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row-start-3 row-end-4">
                <BusinessCardFooter showFooter={true} theme="default" />
              </div>
            </div>
          </div>
        }
        backContent={null}
        isPreview={false}
        isFlipping={false}
        showEmailLogin={false}
        className="max-w-[360px] w-full"
        fullHeight={true}
      />
    );
  }

  const createScrollableContainer = (children: React.ReactNode) => (
    <div className="w-full h-full" translate="no">
      <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
        <div className="row-start-1 row-end-2 min-h-0">
          <div
            className="h-full overflow-y-auto overscroll-contain touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 backdrop-blur bg-gray-950/90 m-3 sm:m-4">
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const frontContent = (
    <div className="w-full h-full" translate="no">
      <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
        <div className="row-start-1 row-end-2 min-h-0">
          <div
            className="h-full overflow-y-auto overscroll-contain touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0">{entityBrand}</div>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-xl sm:text-2xl font-semibold text-white leading-tight">
                          {entityData.entity_name}
                        </h2>
                        {entityData.entity_description && (
                          <p className="text-sm text-white/70 mt-1">
                            {entityData.entity_description}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <LanguageSelector variant="compact" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
              {eventSettings.event_image_url && (
                <div className="w-full aspect-video">
                  <img
                    src={eventSettings.event_image_url}
                    alt={eventSettings.event_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white leading-tight">
                      {eventSettings.event_name}
                    </h3>
                    {eventSettings.event_description && (
                      <p className="text-sm text-white/70 mt-2">
                        {eventSettings.event_description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row-start-2 row-end-3 overflow-hidden">
          <div className="px-3 sm:px-4 pb-3 sm:pb-4 relative z-10 flex justify-center">
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white hover:scale-105 transition-all duration-200"
            >
              <ImExit size={16} />
            </button>
          </div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter showFooter={true} theme="default" />
        </div>
      </div>
    </div>
  );

  const backContent = createScrollableContainer(
    <>
      <div className="mb-4 flex items-center justify-between">
        <BackButton
          onClick={() => navigate(`/event/${processId}`)}
          label={t("event.dashboard.backToEvent")}
        />
        <LanguageSelector variant="compact" />
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
          <HiCheckCircle className="text-green-500" size={40} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">
            {t("event.dashboard.ticketConfirmed")}
          </h1>
        </div>
      </div>

      <div className="bg-black/20 border border-white/15 rounded-lg p-4 mb-4">
        <h2 className="font-bold text-base text-white mb-2">
          {eventSettings.event_name}
        </h2>
        <p className="text-xs text-white/60 text-end">
          {t("event.dashboard.organizedBy", {
            entityName: entityData.entity_name,
          })}
        </p>
      </div>

      <div className="border-t border-white/20 pt-4 mt-4">
        <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
          <HiTicket size={22} />
          {ticketData?.ticket_quantity || 1}{" "}
          {ticketData?.ticket_quantity === 1
            ? t("event.dashboard.ticketRegistered")
            : t("event.dashboard.ticketsRegistered")}
        </h3>

        <div className="space-y-2">
          {ticketData?.ticket_holder_names &&
          ticketData.ticket_holder_names.length > 0 ? (
            ticketData.ticket_holder_names.map((email, index) => {
              const isPurchaser =
                index === 0 && !ticketData?.purchaser_already_has_ticket;
              const isEmailValidated =
                isPurchaser ||
                ticketData?.attendee_email_validation_status?.[email] === true;
              const hasTicket = isPurchaser
                ? ticketData?.current_purchaser_has_ticket === true
                : ticketData?.current_attendee_has_ticket?.[email] === true;

              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <HiTicket
                      size={22}
                      className={hasTicket ? "text-green-600" : "text-blue-600"}
                    />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {email}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      {isPurchaser && (
                        <StatusBadge color="success" size="xs">
                          {t("event.dashboard.you")}
                        </StatusBadge>
                      )}
                      {!isPurchaser && !isEmailValidated && (
                        <div style={blinkStyle}>
                          <StatusBadge color="warning" size="xs">
                            {t("event.dashboard.emailValidationRequired")}
                          </StatusBadge>
                        </div>
                      )}
                      {!isPurchaser && isEmailValidated && (
                        <StatusBadge color="success" size="xs">
                          {t("event.dashboard.emailValidated")}
                        </StatusBadge>
                      )}
                    </div>
                    {hasTicket ? (
                      <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1 font-medium">
                        <HiCheckCircle size={14} className="shrink-0" />{" "}
                        {isPurchaser
                          ? t("event.dashboard.registrationConfirmed")
                          : t("event.dashboard.registrationConfirmedOther")}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1 font-medium">
                        <HiExclamationCircle size={14} className="shrink-0" />{" "}
                        {t("event.dashboard.mustValidateEmail")}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
              <HiTicket
                size={22}
                className="text-green-600 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {userEmail}
                </p>
                <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1 font-medium">
                  <HiCheckCircle size={14} />{" "}
                  {t("event.dashboard.yourRegistrationConfirmed")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/20 pt-4 mt-4">
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <HiCheckCircle size={24} />
          {t("event.dashboard.howToAccess")}
        </h2>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="font-bold text-gray-900 mb-2 text-sm">
            {t("event.dashboard.noPaperNoQr")}
          </p>
          <p className="text-gray-700 text-sm font-medium">
            {t("event.dashboard.justShowUp")}
          </p>
        </div>
      </div>

      {eventSettings.location && (
        <div className="border-t border-white/20 pt-4 mt-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-700 text-sm whitespace-pre-line flex items-start gap-2">
              <MdOutlineLocationOn size={18} className="flex-shrink-0 mt-0.5" />
              <span className="break-words min-w-0 flex-1">
                {eventSettings.location}
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="border-t border-white/20 pt-4 mt-4">
        <AddToGoogleCalendarButton
          title={eventSettings.event_name}
          description={eventSettings.event_description}
          location={eventSettings.location}
          startDateTime={purchasedTicketType?.settings?.sale_start_date}
          endDateTime={purchasedTicketType?.settings?.sale_end_date}
          buttonText={t("event.dashboard.addToCalendar") as string}
          className="w-full"
          variant="secondary"
        />
      </div>

      {ticketData?.ticket_holder_names &&
        ticketData.ticket_holder_names.length > 1 &&
        ticketData.ticket_holder_names
          .slice(1)
          .some(
            (email) =>
              ticketData?.attendee_email_validation_status?.[email] !== true
          ) && (
          <div className="border-t border-white/20 pt-4 mt-4">
            <div className="bg-amber-100 border-2 border-amber-600 rounded-lg p-4 shadow-sm">
              <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2 text-base">
                <HiExclamationCircle
                  className="text-amber-600 flex-shrink-0"
                  size={26}
                />
                {t("event.dashboard.validationNotice.title")}
              </h3>
              <div className="text-sm text-gray-800 space-y-3">
                <div className="bg-amber-200 border border-amber-500 rounded-lg p-3 shadow-sm">
                  <div className="flex items-start gap-3">
                    <HiMail
                      className="text-amber-700 flex-shrink-0 mt-0.5"
                      size={22}
                    />
                    <div>
                      <p className="font-bold text-base mb-1.5 text-amber-900">
                        {t("event.dashboard.validationNotice.warning")}
                      </p>
                      <p className="text-gray-800 font-medium">
                        {t("event.dashboard.validationNotice.description")}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="font-bold mb-2 text-gray-900">
                    {t("event.dashboard.validationNotice.whatToDo")}
                  </p>
                  <ul className="space-y-2 ms-4 text-gray-800 font-medium">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-amber-700">1.</span>
                      <span>{t("event.dashboard.validationNotice.step1")}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-amber-700">2.</span>
                      <span>{t("event.dashboard.validationNotice.step2")}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-amber-700">3.</span>
                      <span>{t("event.dashboard.validationNotice.step3")}</span>
                    </li>
                  </ul>
                </div>
                <p className="font-bold text-amber-900 bg-amber-300 border border-amber-600 p-3 rounded text-center shadow-sm">
                  {t("event.dashboard.validationNotice.reminder")}
                </p>
              </div>
            </div>
          </div>
        )}
    </>
  );

  return (
    <>
      <style>{styleSheet}</style>
      <LoyaltyCardContainer
        frontContent={frontContent}
        backContent={backContent}
        isPreview={false}
        isFlipping={false}
        showEmailLogin={true}
        className="max-w-[360px] w-full"
        fullHeight={true}
      />
    </>
  );
};

export default EventDashboardApp;
